import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { news, newsCategories } from "@/mocks/news";
import NewsListItem from "@/components/feature/NewsListItem";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageContainer from "@/components/base/PageContainer";
import SectionHeading from "@/components/base/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 6;

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "all";
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  usePageMeta(
    "影视影院 - 最新影视资讯",
    "影视影院资讯频道聚合最新影视新闻、影视评论与行业报道，及时了解影视行业动态与热门作品讨论。",
    "影视资讯,影视新闻,影视评论,行业报道,影视影院"
  );

  const { list, totalPages, total } = useMemo(() => {
    const filtered =
      category === "all" ? news : news.filter((item) => item.category === category);
    const sorted = [...filtered].sort((a, b) => b.date.localeCompare(a.date));
    const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const start = (page - 1) * PAGE_SIZE;
    return { list: sorted.slice(start, start + PAGE_SIZE), totalPages: pages, total: sorted.length };
  }, [category, page]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const updateCategory = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("category");
    else next.set("category", value);
    next.delete("page");
    setSearchParams(next);
  };

  const hotNews = [...news].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5);

  return (
    <div className="w-full bg-background-100">
      <PageContainer className="py-6 md:py-8">
        <Breadcrumb items={[{ label: "影视资讯" }]} />

        <div className="mt-4">
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
            影视资讯
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            汇总影视新闻、影视评论与行业报道，掌握最新影视动态。
          </p>
        </div>

        <div className="mt-5 flex items-center gap-1 p-1 rounded-full bg-background-200 w-fit max-w-full overflow-x-auto no-scrollbar">
          {newsCategories.map((item) => (
            <Link
              key={item.value}
              to={item.value === "all" ? "/news" : `/news?category=${item.value}`}
              className={`px-4 py-1.5 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                category === item.value
                  ? "bg-primary-600 text-white"
                  : "text-foreground-600 hover:text-primary-700"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2">
            {list.length > 0 ? (
              <>
                <div className="flex flex-col gap-3">
                  {list.map((item) => (
                    <NewsListItem key={item.id} item={item} />
                  ))}
                </div>
                <div className="mt-8">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onChange={(next) => {
                      const params = new URLSearchParams(searchParams);
                      if (next === 1) params.delete("page");
                      else params.set("page", String(next));
                      setSearchParams(params);
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="py-16 flex flex-col items-center text-center rounded-lg bg-background-50 border border-background-200">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
                  <i className="ri-article-line text-3xl" />
                </span>
                <p className="text-lg font-heading font-bold text-foreground-800">暂无相关资讯</p>
                <p className="mt-2 text-sm text-foreground-500">该分类下暂时没有内容，换个分类看看吧。</p>
                <Link
                  to="/news"
                  className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-label transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line" />
                  查看全部资讯
                </Link>
              </div>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg bg-background-50 border border-background-200 p-4">
              <SectionHeading title="热门阅读" icon="ri-fire-line" />
              <ul className="flex flex-col divide-y divide-background-200">
                {hotNews.map((item, index) => (
                  <li key={item.id}>
                    <Link
                      to={`/news/detail/${item.id}`}
                      className="group flex gap-3 py-3 cursor-pointer"
                    >
                      <span
                        className={`shrink-0 w-5 h-5 mt-0.5 rounded text-xs font-label font-bold flex items-center justify-center ${
                          index < 3 ? "bg-accent-500 text-white" : "bg-background-200 text-foreground-500"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors">
                          {item.title}
                        </p>
                        <p className="mt-1 text-xs text-foreground-400">{item.date}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 rounded-lg bg-primary-950 text-background-200 p-5">
              <i className="ri-megaphone-line text-2xl text-accent-400" />
              <h3 className="mt-2 font-heading font-bold text-white">资讯说明</h3>
              <p className="mt-2 text-xs leading-relaxed text-background-300">
                本频道所有新闻、评论与报道均为虚构演示内容，仅用于界面与交互展示，不代表任何真实事件或作品。
              </p>
            </div>
          </aside>
        </div>
      </PageContainer>
    </div>
  );
}